/**
 * Neural Network 3D Visualization
 * Creates and manages 3D neuron nodes and synapse connections
 */

import * as THREE from 'three'
import gsap from 'gsap'
import { createNoise3D } from 'simplex-noise'

export class NeuralNetwork {
  constructor(scene, data, options = {}) {
    this.scene = scene
    this.data = data
    this.options = {
      neuronBaseSize: options.neuronBaseSize || 0.3,
      synapseWidth: options.synapseWidth || 2,
      glowIntensity: options.glowIntensity || 1.5,
      animationSpeed: options.animationSpeed || 1.0,
      ...options
    }

    this.neurons = new Map()
    this.synapses = new Map()
    this.neuronMeshes = []
    this.synapseMeshes = []
    
    this.noise3D = createNoise3D()
    this.time = 0
    
    this.hoveredNeuron = null
    this.selectedNeuron = null
    
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()

    this.createNeurons()
    this.createSynapses()
    this.createAmbientParticles()
  }

  createNeurons() {
    const categories = this.data.categories

    this.data.nodes.forEach(node => {
      const category = categories[node.category] || categories.core
      const size = this.options.neuronBaseSize * category.size * (0.8 + node.importance * 0.4)

      // Create neuron core
      const coreGeometry = new THREE.SphereGeometry(size, 32, 32)
      const coreMaterial = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(category.color),
        emissive: new THREE.Color(category.color),
        emissiveIntensity: 0.3,
        metalness: 0.1,
        roughness: 0.2,
        transparent: true,
        opacity: 0.9,
        envMapIntensity: 1.0
      })
      
      const coreMesh = new THREE.Mesh(coreGeometry, coreMaterial)
      coreMesh.position.set(node.position.x, node.position.y, node.position.z)
      coreMesh.userData = { nodeId: node.id, node }
      coreMesh.layers.enable(1)

      // Create outer glow shell
      const glowGeometry = new THREE.SphereGeometry(size * 1.4, 32, 32)
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(category.color),
        transparent: true,
        opacity: 0.15 * category.glowIntensity,
        side: THREE.BackSide
      })
      
      const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial)
      glowMesh.position.copy(coreMesh.position)

      // Create pulsing ring
      const ringGeometry = new THREE.TorusGeometry(size * 1.2, 0.02, 16, 64)
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(category.color),
        transparent: true,
        opacity: 0.4
      })
      
      const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial)
      ringMesh.position.copy(coreMesh.position)

      // Group all parts
      const neuronGroup = new THREE.Group()
      neuronGroup.add(coreMesh)
      neuronGroup.add(glowMesh)
      neuronGroup.add(ringMesh)
      neuronGroup.userData = { nodeId: node.id, node, core: coreMesh, glow: glowMesh, ring: ringMesh }

      this.scene.add(neuronGroup)
      this.neurons.set(node.id, neuronGroup)
      this.neuronMeshes.push(coreMesh)
    })
  }

  createSynapses() {
    const createdPairs = new Set()

    this.data.nodes.forEach(node => {
      if (!node.connections) return

      node.connections.forEach(targetId => {
        const pairKey = [node.id, targetId].sort().join('-')
        if (createdPairs.has(pairKey)) return
        createdPairs.add(pairKey)

        const targetNode = this.data.nodes.find(n => n.id === targetId)
        if (!targetNode) return

        const synapse = this.createSynapse(node, targetNode)
        this.synapses.set(pairKey, synapse)
      })
    })
  }

  createSynapse(fromNode, toNode) {
    const start = new THREE.Vector3(
      fromNode.position.x,
      fromNode.position.y,
      fromNode.position.z
    )
    const end = new THREE.Vector3(
      toNode.position.x,
      toNode.position.y,
      toNode.position.z
    )

    // Create curved path
    const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5)
    const direction = new THREE.Vector3().subVectors(end, start)
    const perpendicular = new THREE.Vector3(-direction.y, direction.x, direction.z * 0.5).normalize()
    
    const curveOffset = direction.length() * 0.15
    midPoint.add(perpendicular.multiplyScalar(curveOffset))

    const curve = new THREE.QuadraticBezierCurve3(start, midPoint, end)
    const points = curve.getPoints(50)

    // Main synapse line
    const geometry = new THREE.BufferGeometry().setFromPoints(points)
    const material = new THREE.LineBasicMaterial({
      color: 0x4a5568,
      transparent: true,
      opacity: 0.3,
      linewidth: this.options.synapseWidth
    })

    const line = new THREE.Line(geometry, material)
    line.userData = { fromId: fromNode.id, toId: toNode.id, curve }

    // Create flowing particles along synapse
    const particleGroup = this.createSynapseParticles(curve)

    const synapseGroup = new THREE.Group()
    synapseGroup.add(line)
    synapseGroup.add(particleGroup)
    synapseGroup.userData = { fromId: fromNode.id, toId: toNode.id, line, particles: particleGroup }

    this.scene.add(synapseGroup)
    this.synapseMeshes.push(synapseGroup)

    return synapseGroup
  }

  createSynapseParticles(curve) {
    const particleCount = 5
    const particleGeometry = new THREE.SphereGeometry(0.03, 8, 8)
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: 0x00d4ff,
      transparent: true,
      opacity: 0.6
    })

    const group = new THREE.Group()

    for (let i = 0; i < particleCount; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial.clone())
      particle.userData = { progress: i / particleCount, curve }
      group.add(particle)
    }

    return group
  }

  createAmbientParticles() {
    const particleCount = 200
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)
    const sizes = new Float32Array(particleCount)

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20
      positions[i * 3 + 1] = (Math.random() - 0.5) * 15
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20

      const color = new THREE.Color().setHSL(0.55 + Math.random() * 0.1, 0.8, 0.6)
      colors[i * 3] = color.r
      colors[i * 3 + 1] = color.g
      colors[i * 3 + 2] = color.b

      sizes[i] = Math.random() * 3 + 1
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

    const material = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    })

    this.ambientParticles = new THREE.Points(geometry, material)
    this.scene.add(this.ambientParticles)
  }

  highlightNeuron(nodeId) {
    if (this.hoveredNeuron === nodeId) return

    // Reset previous highlight
    if (this.hoveredNeuron) {
      this.unhighlightNeuron(this.hoveredNeuron)
    }

    this.hoveredNeuron = nodeId
    const neuronGroup = this.neurons.get(nodeId)
    if (!neuronGroup) return

    const { core, glow, ring, node } = neuronGroup.userData

    gsap.to(core.scale, {
      x: 1.3,
      y: 1.3,
      z: 1.3,
      duration: 0.3,
      ease: 'back.out(1.7)'
    })

    gsap.to(glow.material, {
      opacity: 0.4,
      duration: 0.3
    })

    gsap.to(ring.rotation, {
      z: Math.PI * 2,
      duration: 2,
      repeat: -1,
      ease: 'none'
    })

    // Highlight connected synapses
    if (node.connections) {
      node.connections.forEach(targetId => {
        this.highlightSynapse(nodeId, targetId)
      })
    }
  }

  unhighlightNeuron(nodeId) {
    const neuronGroup = this.neurons.get(nodeId)
    if (!neuronGroup) return

    const { core, glow, ring, node } = neuronGroup.userData

    gsap.to(core.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.3,
      ease: 'power2.out'
    })

    gsap.to(glow.material, {
      opacity: 0.15,
      duration: 0.3
    })

    gsap.killTweensOf(ring.rotation)
    
    // Unhighlight connected synapses
    if (node.connections) {
      node.connections.forEach(targetId => {
        this.unhighlightSynapse(nodeId, targetId)
      })
    }

    if (this.hoveredNeuron === nodeId) {
      this.hoveredNeuron = null
    }
  }

  highlightSynapse(fromId, toId) {
    const pairKey = [fromId, toId].sort().join('-')
    const synapse = this.synapses.get(pairKey)
    if (!synapse) return

    const { line } = synapse.userData

    gsap.to(line.material, {
      opacity: 0.8,
      duration: 0.3
    })

    gsap.to(line.material.color, {
      r: 0,
      g: 0.83,
      b: 1,
      duration: 0.3
    })
  }

  unhighlightSynapse(fromId, toId) {
    const pairKey = [fromId, toId].sort().join('-')
    const synapse = this.synapses.get(pairKey)
    if (!synapse) return

    const { line } = synapse.userData

    gsap.to(line.material, {
      opacity: 0.3,
      duration: 0.3
    })

    gsap.to(line.material.color, {
      r: 0.29,
      g: 0.33,
      b: 0.41,
      duration: 0.3
    })
  }

  selectNeuron(nodeId) {
    if (this.selectedNeuron) {
      const prev = this.neurons.get(this.selectedNeuron)
      if (prev) {
        const { core } = prev.userData
        core.material.emissiveIntensity = 0.3
      }
    }

    this.selectedNeuron = nodeId
    const neuronGroup = this.neurons.get(nodeId)
    if (!neuronGroup) return

    const { core } = neuronGroup.userData

    gsap.to(core.material, {
      emissiveIntensity: 0.8,
      duration: 0.5
    })

    return neuronGroup.userData.node
  }

  getNodeAtPosition(x, y, camera) {
    this.mouse.x = (x / window.innerWidth) * 2 - 1
    this.mouse.y = -(y / window.innerHeight) * 2 + 1

    this.raycaster.setFromCamera(this.mouse, camera)
    const intersects = this.raycaster.intersectObjects(this.neuronMeshes)

    if (intersects.length > 0) {
      return intersects[0].object.userData.node
    }

    return null
  }

  update(deltaTime) {
    this.time += deltaTime * this.options.animationSpeed

    // Animate neurons
    this.neurons.forEach((group, nodeId) => {
      const { core, ring, node } = group.userData
      
      // Organic floating motion
      const noiseX = this.noise3D(node.position.x * 0.5, this.time * 0.3, 0) * 0.05
      const noiseY = this.noise3D(0, node.position.y * 0.5, this.time * 0.3) * 0.05
      const noiseZ = this.noise3D(this.time * 0.3, 0, node.position.z * 0.5) * 0.05

      core.position.x = node.position.x + noiseX
      core.position.y = node.position.y + noiseY
      core.position.z = node.position.z + noiseZ

      // Pulse effect based on importance
      const pulse = Math.sin(this.time * 2 + node.importance * 10) * 0.1
      core.material.emissiveIntensity = 0.3 + pulse * node.importance

      // Ring rotation
      ring.rotation.x = this.time * 0.5
      ring.rotation.y = this.time * 0.3
      ring.position.copy(core.position)

      group.userData.glow.position.copy(core.position)
    })

    // Animate synapse particles
    this.synapseMeshes.forEach(synapseGroup => {
      const particles = synapseGroup.userData.particles
      particles.children.forEach(particle => {
        particle.userData.progress += deltaTime * 0.2
        if (particle.userData.progress > 1) {
          particle.userData.progress = 0
        }
        
        const point = particle.userData.curve.getPoint(particle.userData.progress)
        particle.position.copy(point)
        particle.material.opacity = 0.3 + Math.sin(particle.userData.progress * Math.PI) * 0.4
      })
    })

    // Animate ambient particles
    if (this.ambientParticles) {
      const positions = this.ambientParticles.geometry.attributes.position.array
      for (let i = 0; i < positions.length; i += 3) {
        positions[i + 1] += Math.sin(this.time + i) * 0.001
      }
      this.ambientParticles.geometry.attributes.position.needsUpdate = true
      this.ambientParticles.rotation.y = this.time * 0.02
    }
  }

  getNeuronPosition(nodeId) {
    const neuron = this.neurons.get(nodeId)
    if (!neuron) return null
    return neuron.userData.core.position.clone()
  }

  getNodeById(nodeId) {
    return this.data.nodes.find(n => n.id === nodeId)
  }

  dispose() {
    this.neurons.forEach(group => {
      group.children.forEach(child => {
        if (child.geometry) child.geometry.dispose()
        if (child.material) child.material.dispose()
      })
      this.scene.remove(group)
    })

    this.synapseMeshes.forEach(synapse => {
      synapse.children.forEach(child => {
        if (child.geometry) child.geometry.dispose()
        if (child.material) child.material.dispose()
      })
      this.scene.remove(synapse)
    })

    if (this.ambientParticles) {
      this.ambientParticles.geometry.dispose()
      this.ambientParticles.material.dispose()
      this.scene.remove(this.ambientParticles)
    }

    this.neurons.clear()
    this.synapses.clear()
  }
}
